/**
 * Title matching for the people-chooser fields (userSelect / personSearch /
 * peopleMultiSelect), whose lists can be limited by roster title — e.g. a "Legal
 * secretary" picker that only offers people whose roster title says so.
 *
 * Roster titles come from the NMMExport feed's AD_Title column and arrive messy:
 * combo roles separated by "/" or "\" ("Legal Secretary/Paralegal",
 * "Paralegal\Legal Assistant"), stray double spaces, trailing spaces ("Associate "),
 * inconsistent case. Matching is therefore done on normalized TOKENS — a title is split
 * on both slashes, and a person matches a filter when ANY of their tokens equals ANY of
 * the filter's tokens, case- and whitespace-insensitively. "Legal Secretary/Paralegal"
 * matches a "Legal Secretary" filter and a "Paralegal" filter alike.
 *
 * An empty/absent filter matches everyone — the historical, unfiltered chooser. A person
 * with NO title matches nothing once a filter is set: an unlabeled roster row shouldn't
 * slip into a deliberately narrowed list.
 */

const normalizeToken = (raw) => String(raw || '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * "Legal Secretary/Paralegal" -> ['legal secretary', 'paralegal'];
 * "Partner, General Counsel" -> ['partner', 'general counsel'].
 * Commas separate combined titles too — the feed writes the GC pair that way.
 */
export function titleTokens(title) {
  return String(title || '')
    .split(/[\\/,]/)
    .map(normalizeToken)
    .filter(Boolean);
}

/** True when the person's title matches the filter (or the filter is empty/absent). */
export function personMatchesTitles(person, titles) {
  if (!Array.isArray(titles) || titles.length === 0) return true;
  const wanted = new Set(titles.flatMap(titleTokens));
  if (wanted.size === 0) return true;
  return titleTokens(person && person.title).some((token) => wanted.has(token));
}

/** Convenience for the choosers: the roster narrowed to a field's titleFilter. */
export function filterPeopleByTitles(people, titles) {
  const list = Array.isArray(people) ? people : [];
  if (!Array.isArray(titles) || titles.length === 0) return list;
  return list.filter((person) => personMatchesTitles(person, titles));
}

/**
 * Every distinct normalized title token across the roster, Title-Cased for display,
 * sorted — what Field Builder offers as the filter choices. `extra` folds in tokens
 * already saved on a field whose holders have since left the roster, so a stale filter
 * entry stays visible and removable instead of becoming invisible configuration.
 */
export function distinctTitleTokens(people, extra = []) {
  const tokens = new Set();
  for (const person of Array.isArray(people) ? people : []) {
    for (const token of titleTokens(person && person.title)) tokens.add(token);
  }
  for (const title of Array.isArray(extra) ? extra : []) {
    for (const token of titleTokens(title)) tokens.add(token);
  }
  const display = (token) => token.replace(/\b([a-z])/g, (m, c) => c.toUpperCase());
  return [...tokens].sort().map(display);
}
